<style scoped lang="less">
.modal-title {
    text-transform: uppercase;
}
</style>
<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h2 class="modal-title">Workflow Manual Override</h2>
            <button type="button" class="close icon icon-close" aria-hidden="true" @click="$emit('close')" autofocus :disabled="submitting" aria-label="Close" />
        </div>
        <div class="modal-body">
            <div v-if="submitting">
                <LoadingWheel :width="100" />
            </div>
            <template v-else>
                <div v-if="messages.length > 0" class="invalid-feedback d-block">
                    <span>Validation:</span>
                    <ul>
                        <li v-for="message in messages">{{ message }}</li>
                    </ul>
                </div>
                <div describedby-text class="alert alert-danger">These actions allow you to bypass safety controls. This is inherently unsafe, and may cause unexpected issues.</div>
                <div class="form-group">
                    <label for="ManualOverrideType">Action To Take:</label>
                    <select id="ManualOverrideType" class="form-control" v-model="type">
                        <option selected :value="null">-- Select an action --</option>
                        <option value="ForceWorkflowStatus">Move To Step</option>
                        <option value="ForceDefinitionUpdate" v-if="canUpgradeDefinition">Update To Definition</option>
                        <option value="EndWorkflow">End Workflow</option>
                        <option value="DeleteWorkflow">Delete Submission</option>
                    </select>
                </div>
                <div class="form-group" v-show="type == 'ForceDefinitionUpdate'">
                    <label for="ManualOverrideRevision">Target Revision:</label>
                    <span v-if="possibleUpgradeRevisions == null"> Loading... </span>
                    <span v-else-if="possibleUpgradeRevisions.length == 0"> No possible upgrades </span>
                    <span v-else-if="possibleUpgradeRevisions.length == 1"> {{ localeDateTime(possibleUpgradeRevisions[0].Updated) }} {{ possibleUpgradeRevisions[0].UpdateMessage != null && possibleUpgradeRevisions[0].UpdateMessage != "" ? " with comment: " : "" }} {{ possibleUpgradeRevisions[0].UpdateMessage }} </span>
                    <select id="ManualOverrideRevision" class="form-control" v-model="manualOverrideRevision" v-else>
                        <option selected :value="null">-- Select an revision --</option>
                        <option v-for="revision in possibleUpgradeRevisions" :value="revision.FormHash">{{ localeDateTime(revision.Updated) }} {{ revision.UpdateMessage != null && revision.UpdateMessage != "" ? " with comment: " : "" }} {{ revision.UpdateMessage }}</option>
                    </select>
                </div>
                <div class="form-group" v-show="type == 'ForceWorkflowStatus'">
                    <label for="ManualOverrideStep">Step:</label>
                    <select id="ManualOverrideStep" class="form-control" v-model="forceStatus">
                        <option selected :value="null">-- Select an step --</option>
                        <option v-for="status in possibleStatusesExcludingCurrent" :value="status.value">{{ status.adminText || status.text || status.value }}</option>
                    </select>
                </div>
            </template>
        </div>
        <div class="modal-footer">
            <button id="returnBtn" class="btn btn-primary" @click="submit()" :disabled="submitting">Execute</button>
            <button id="cancelBtn" class="btn btn-outline-secondary" @click="$emit('close')" :disabled="submitting">Cancel</button>
        </div>
    </div>
</template>
<script lang="ts">
import { defineComponent, ref, watch, computed, SetupContext, inject } from "vue";
import { localeDateTime } from "@/lib/localeDatetime.3";
import LoadingWheel from "@/lib/LoadingWheel.3.vue";
import WorkflowManualOverrideConfirmModal from "./WorkflowManualOverrideConfirm.3.vue";
import { Revision } from "@cs/Revision";
import { usePageStore } from "../pageState/store";
import { ManualOverrideInvokeResult } from "@cs-ts/manual-override-invoke-result";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import { showModalInjectKey, showModal } from "@/AppShell.3.vue.InjectKeys";
type Props = {
    returnFunction: (type: string, data: string | null) => Promise<ManualOverrideInvokeResult>;
} & DialogProps;
export default defineComponent({
    components: {
        LoadingWheel,
    },
    props: ["returnFunction", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        const dialogMixin = dialogSetup(containerRef, props);

        const type = ref(null as string | null);
        const forceStatus = ref(null);
        const submitting = ref(false);
        const messages = ref([] as string[]);
        const possibleUpgradeRevisions = ref(null as null | Revision[]);
        const manualOverrideRevision = ref(null);

        const pageStore = usePageStore();

        const possibleStatuses = computed(() => pageStore.possibleStatuses);
        const status = computed(() => pageStore.status);
        const canUpgradeDefinition = computed(() => pageStore.canUpgradeDefinition);

        const possibleStatusesExcludingCurrent = computed(() => {
            return possibleStatuses.value.filter(i => i.value != status.value);
        });

        const showModal = inject(showModalInjectKey) as showModal;

        watch(
            () => type.value,
            (newV: string | null, _: string | null) => {
                if (newV == "ForceDefinitionUpdate" && possibleUpgradeRevisions.value == null) {
                    // TODO get string from CS bridge
                    (async () => {
                        var r = await pageStore.getWorkflowManualOverrideGetPossibleUpgradeRevisions();
                        if (r.success) {
                            possibleUpgradeRevisions.value = r.revisions;
                        } else {
                            throw new Error(newV + "\n" + JSON.stringify(r));
                        }
                    })();
                }
            },
        );

        const submit = async () => {
            if (type.value == null) {
                messages.value = ["Type is required"];
                return;
            }
            dialogMixin.focusTrapSetEnabled(false);
            showModal(
                WorkflowManualOverrideConfirmModal,
                {
                    returnFunction: async () => {
                        submitting.value = true;
                        const data = type.value == "ForceWorkflowStatus" ? forceStatus.value : type.value == "ForceDefinitionUpdate" ? manualOverrideRevision.value || (possibleUpgradeRevisions.value?.length == 1 ? possibleUpgradeRevisions.value[0].FormHash : null) : null;
                        const result = await props.returnFunction(type.value!, data);
                        submitting.value = false;
                        if (result.success) {
                            emit("close");
                        } else {
                            messages.value = result.failureMessages;
                        }
                    },
                },
                //{
                //    height: "auto",
                //    width: Math.min(0.9 * window.innerWidth, 600),
                //    clickToClose: false,
                //},
                {
                    "before-close"() {
                        dialogMixin.focusTrapSetEnabled(true);
                    },
                },
            );
        };

        return {
            container: containerRef,
            submitting,
            messages,
            type,
            canUpgradeDefinition,
            possibleUpgradeRevisions,
            manualOverrideRevision,
            forceStatus,
            possibleStatusesExcludingCurrent,
            localeDateTime,

            submit,
        };
    },
});
</script>
